import Button from 'components/Button';
import DashboardGrid from 'components/DashboardGrid';
import useDashboardGridChildren, {
  MODES,
} from 'components/DashboardGrid/hooks/useDashboardChildren';
import useDashboardInteraction from 'components/DashboardGrid/hooks/useDashboardInteraction';
import {
  useStaticContentBlock,
  isStaticContentDashlet,
} from 'components/DashboardGrid/hooks/useStaticContentBlock';
import {
  usePluginContentBlock,
  isPluginContentDashlet,
} from 'components/DashboardGrid/hooks/usePluginContentBlock';
import { useCustomCodeBlock } from 'components/DashboardGrid/hooks/useCustomCodeBlock';
import { isCustomCodeDashlet } from 'components/DashboardGrid/hooks/customCodeBlock';
import { CustomCodeBlockEditor } from './components/CustomCodeBlock/CustomCodeBlockEditor';
import {
  DashboardGridContainerBox,
  DashboardGridPageContainer,
} from 'components/DashboardGrid/styles';
import SubNavigationBar from 'components/Kizen/SubNavigationBar';
import useModal from 'components/Modals/useModal';
import EditDashboardsButton from 'pages/Dashboard/components/EditDashboardsButton';
import { useCallback, useMemo, useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams, Redirect } from 'react-router-dom';
import { StyledNav, StyledSwitch, SwitchContainer } from './styles';
import { StyledSubNavLabel } from 'pages/Dashboard/components/SubNav/styles';
import { ActiveStateModal } from './components/ActiveModal';
import BlockModal from './components/PluginBlock/modal';
import PluginBlockWizard from './components/PluginBlock/PluginBlockWizard';
import {
  createDashboardQuery,
  fetchDashboardsQuery,
} from 'components/AccessRequests/useDashboardList';
import { toastVariant, useToast } from 'components/ToastProvider';
import { DASHBOARD } from 'queries/query-keys';
import { useModelQuery } from 'queries/models/custom-objects';
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { getBusinessClientObject } from 'store/authentication/selectors';
import { getSortedDashboards } from 'components/DashboardGrid/utils';
import Loader from 'components/Kizen/Loader';
import useDashboardConfig from 'pages/Dashboard/hooks/useDashboardConfig';
import { buildSharingSettingsForAPI } from 'utility/sharing';
import CreateLayoutModal from 'components/DashboardGrid/CreateLayoutModal';
import { useHomepageOptimisticUpdate } from './hooks/useHomepageOptimisticUpdate';
import { DEFAULT_NAME_KEY, buildDashletConfig } from 'pages/Dashboard/util';
import { IntermediateDashlet } from './components/IntermediateDashlet';
import { getDashletDimensions } from 'components/DashboardGrid/dimensions';
import { AREA_RESPONSES } from 'components/Wizards/Dashlet/types';
import { v4 as uuidv4 } from 'uuid';
import { useSelector } from 'react-redux';
import { useHomepageAdminNotice } from './hooks/useHomepageAdminNotice';
import { useSetTitleOnLoad } from 'hooks/useSetTitleOnLoad';
import { DASHBOARD_BREAKPOINT_NAMES } from 'components/DashboardGrid/types';
import { breakpoints, useBreakpoint } from 'app/spacing';
import { DeleteConfirmationModal } from './components/DeleteConfirmationModal';
import DashletModal from 'pages/Dashboard/components/Dashlet/modal';
import ChartModal from 'components/ObjectCharts/modal';
import { DashboardStateWrapper } from '../Dashboard/context/persistentState';
import { ErrorMessage } from 'components/ErrorMessage';
import {
  BreakpointContainer,
  DashboardErrorCodeText,
  DashboardWarningText,
  SinglePromptPopoverWrapper,
} from 'pages/Dashboard/styles';
import { AREA_RESPONSES as CHART_AREA_RESPONSES } from 'components/Wizards/RTDV/types';
import { DataManagerWrapper } from 'pages/Dashboard/context/dataManager/context';
import getDashboardUrl from 'utility/getDashboardUrl';
import { deferExecution } from 'utility/defer';
import { editableDashlet } from 'pages/Dashboard/util';
import { HomepageBuilder } from 'ts-components/StaticBlockBuilder/HomepageBuilder';
import { isFlagEnabled } from 'debug/flags';
import {
  DashboardStyleSettingsProvider,
  DEFAULT_DASHLET_STYLE_SETTINGS,
} from 'ts-components/StyleSettings';
import { getDashletWithMergedStyles } from 'ts-components/StyleSettings/utils';
import { useAIFeatures, AI_FEATURE_CONFIG_KEYS } from 'hooks/useAIFeatures';
import { FLOW_TYPE, GUARD_RAIL_CODES } from 'ts-components/Conversation';
import { AiExpandableSinglePrompt } from 'ts-components/AiExpandableSinglePrompt';
import {
  OverflowPortalContext,
  PortalContainer,
} from 'components/OverflowPortal';
import { monitoringExceptionHelper } from 'instrumentation/helpers';
import { useAiBlockSession } from 'ts-components/AiBlockPanel/useAiBlockSession';
import {
  AI_BLOCK_TYPE,
  toAiBlockSafeUpdate,
} from 'ts-components/AiBlockPanel/aiBlockSession';
import { PiChatSideLayout } from 'ts-components/PiChat/PiChatSideLayout';

const responseValues = [
  AREA_RESPONSES.PIPELINES,
  AREA_RESPONSES.ACTIVITIES,
  AREA_RESPONSES.EMAILS,
  AREA_RESPONSES.MARKETING,
];
const chartTypes = Object.values(CHART_AREA_RESPONSES);

const HomepagesPage = () => {
  const [errors, setErrors] = useState({});
  const [currentDashlet, setCurrentDashlet] = useState({});
  const [overrideShowDashletMenu, setOverrideShowDashletMenu] = useState(false);
  const [autoOpenAIOverlay, setAutoOpenAIOverlay] = useState(false);
  const [pluginEditDashlet, setPluginEditDashlet] = useState(null);

  const [refreshAfterUpdate, setRefreshAfterUpdate] = useState(false);
  const onRefreshErrorBoundaryRef = useRef(null);
  const { t } = useTranslation();
  const [overflowPortalContainer, setOverflowPortalContainer] = useState(null);
  const [showToast] = useToast();
  const history = useHistory();
  const access = useSelector((s) => s.authentication.access);
  const clientObject = useSelector(getBusinessClientObject);
  const isAdmin = Boolean(
    access?.sections?.homepages_section?.customize_homepages
  );

  const clientObjectId = clientObject?.id;
  const intermediateDeleteState = useRef({});
  const aiOverlayContainerRef = useRef(null);

  const mobile = useBreakpoint(breakpoints.md);
  const { aiFeaturesEnabled } = useAIFeatures(
    AI_FEATURE_CONFIG_KEYS.homepage_builder
  );
  const { aiFeaturesEnabled: dashletAiFeaturesEnabled } = useAIFeatures(
    AI_FEATURE_CONFIG_KEYS.dashlet_builder
  );
  const showSettings = isAdmin;

  const handleFetchDashboards = useCallback(() => {
    return fetchDashboardsQuery('', false, undefined, 'homepage');
  }, []);

  const dashboardsQueryKey = [...DASHBOARD.DASHBOARDS, 'homepage', false];

  const {
    isLoading: myDashboardsLoading,
    data: homepages = [],
    refetch: refetchHomepages,
  } = useQuery({
    queryKey: dashboardsQueryKey,
    queryFn: handleFetchDashboards,
    placeholderData: keepPreviousData,
  });

  const { data: currentDashletModel, isLoading: isCurrentDashletModelLoading } =
    useModelQuery(
      currentDashlet?.customObject,
      Boolean(currentDashlet?.customObject)
    );

  const homepageOptions = useMemo(() => {
    return getSortedDashboards(homepages.filter((h) => !h.hidden));
  }, [homepages]);

  const hasDashboards = homepageOptions.length > 0;

  const {
    loading: configLoading,
    currentDashboard: currentHomepage,
    refetchDashboard,
    dashboardQueryKey,
    currentDashboardPermission: currentHomepagePermission,
    afterCreate,
    setPersistentDashboardState,
    persistentDashboardState,
    dashboardError,
    unsafeDashboardId,
    ranges,
    todayDateInBusinessTimeZone,
    setCurrentDashboardId,
    config,
  } = useDashboardConfig(homepageOptions, false, 'homepage', isAdmin, true);

  const defaultDateFilter = useMemo(() => {
    const index = ranges.findIndex((range) => range.isAllTime);

    if (index >= 0) {
      return {
        start: ranges[index].value[0],
        end: ranges[index].value[1],
        selectedIndex: index,
      };
    }
  }, [ranges]);

  useSetTitleOnLoad(
    currentHomepage
      ? `${currentHomepage.name} - ${t('Homepage')}`
      : t('Homepage')
  );

  const isLoading =
    configLoading || myDashboardsLoading || isCurrentDashletModelLoading;

  const clearToasts = useHomepageAdminNotice(
    isAdmin && !mobile,
    isLoading,
    config
  );

  useEffect(() => {
    const unregisterListener = history.listen(({ pathname }) => {
      if (
        !pathname.startsWith('/home') ||
        pathname.startsWith('/home/settings')
      ) {
        clearToasts();
      }
    }, []);

    return unregisterListener;
  });

  // Homepages have no object of their own (each block picks one), so the AI
  // context carries no objectId
  const {
    renderFocusSlot: renderAiFocusSlot,
    openCreate: openAiBlockCreate,
    openEdit: openAiBlockEdit,
    sessionDashletId: aiSessionDashletId,
    panel: aiBlockPanel,
  } = useAiBlockSession({
    targetType: 'homepage',
    dashboardId: currentHomepage?.id,
    dashlets: currentHomepage?.dashlets,
    dashboardName: currentHomepage?.name,
  });

  const {
    breakpoint,
    setBreakpoint,
    editable,
    rowHeight,
    setContainerWidth,
    dashlets,
    hasEditPermission,
    resizeHandles,
    updateLayouts,
    forceShowUIElements,
    mutateDashlet: mutateChartGroupTypeDashlet,
    duplicateDashlet,
    createDashletMutation,
    updateDashletMutation,
    deleteDashlet,
    draggable,
    columnCount,
    getDashletPosition,
  } = useDashboardInteraction({
    currentPermission: currentHomepagePermission,
    refetchDashboard,
    entityName: t('Block'),
    dashboardQueryKey,
    currentDashboard: currentHomepage,
    isLoading,
    canEdit: true,
    aiSessionDashletId,
  });

  const {
    mutateStaticContentBlock,
    openStaticContentBuilder,
    staticContentBuilderOpen,
    staticContentBuilderProps,
  } = useStaticContentBlock({
    currentDashboard: currentHomepage,
    dashlets,
    getDashletPosition,
    createDashletMutation,
    updateDashletMutation,
    duplicateDashlet,
  });

  const { mutatePluginBlock } = usePluginContentBlock({
    dashlets,
    getDashletPosition,
    createDashletMutation,
    updateDashletMutation,
    duplicateDashlet,
  });

  const { mutateCustomCodeBlock, openCustomCodeEditor, customCodeEditorProps } =
    useCustomCodeBlock({
      currentDashboard: currentHomepage,
      dashlets,
      updateDashletMutation,
      duplicateDashlet,
    });

  const extractGenerationResult = useCallback((response) => {
    const runMetadata = response.run_metadata;
    const guardrailCode = runMetadata?.homepages?.guardrail_code;
    const guardrailMessage = runMetadata?.homepages?.guardrail_message;
    const sessionId = response.session_id || runMetadata?.session_id;
    const generatedHomepage =
      Object.keys(runMetadata?.homepages || {}).length > 0
        ? Object.values(runMetadata?.homepages)?.[0]
        : null;

    return {
      hasResult: !!generatedHomepage,
      sessionId,
      guardrailCode,
      guardrailMessage,
    };
  }, []);

  const redirectToGeneratedHomepage = useCallback(
    async (path) => {
      await refetchHomepages();
      deferExecution(() => {
        history.push(path);
      });
    },
    [history, refetchHomepages]
  );

  const onGenerationSuccess = useCallback(
    (homepageFlowStatusData) => {
      const guardrailCode =
        homepageFlowStatusData.run_metadata?.homepages?.guardrail_code;
      const guardrailMessage =
        homepageFlowStatusData.run_metadata?.homepages?.guardrail_message;
      const navigateUrl = homepageFlowStatusData?.next_actions?.[0]?.url;
      if (navigateUrl) {
        try {
          const url = new URL(navigateUrl);
          const relativePath = url.pathname + url.search;
          if (guardrailCode && guardrailMessage) {
            showToast({
              message: guardrailMessage,
              variant:
                guardrailCode === GUARD_RAIL_CODES.REQUIRES_REVIEW
                  ? toastVariant.ALERT
                  : toastVariant.FAILURE,
              autohide: false,
            });
          } else {
            showToast({
              message: t('Homepage Generated Successfully'),
              variant: toastVariant.SUCCESS,
            });
          }
          setTimeout(() => {
            redirectToGeneratedHomepage(relativePath);
          }, 1500);
          return;
        } catch (error) {
          monitoringExceptionHelper(error, {
            context: 'Homepage Generation Error',
          });
        }
      }

      // Handle one-shot response with run_metadata
      const generatedHomepage =
        Object.keys(homepageFlowStatusData.run_metadata?.homepages || {})
          .length > 0
          ? Object.values(homepageFlowStatusData.run_metadata?.homepages)?.[0]
          : null;

      if (guardrailCode && guardrailMessage) {
        showToast({
          message: guardrailMessage,
          variant:
            guardrailCode === GUARD_RAIL_CODES.REQUIRES_REVIEW
              ? toastVariant.ALERT
              : toastVariant.FAILURE,
          autohide: false,
        });
        if (generatedHomepage) {
          setTimeout(() => {
            redirectToGeneratedHomepage(`/home/${generatedHomepage.id}`);
          }, 1500);
        }
      } else if (generatedHomepage) {
        showToast({
          message: t('Homepage Generated Successfully'),
          variant: toastVariant.SUCCESS,
        });
        setTimeout(() => {
          redirectToGeneratedHomepage(`/home/${generatedHomepage.id}`);
        }, 1500);
      } else {
        showToast({
          message: t('Homepage Generation Failed, Please try again later'),
          variant: toastVariant.FAILURE,
        });
      }
    },
    [showToast, t, redirectToGeneratedHomepage]
  );
  const onGenerationFailure = useCallback(
    (error) => {
      const usageError = error?.response?.data?.run_context?.usage;
      if (usageError && usageError[0]) {
        showToast({
          message: usageError[0],
          variant: toastVariant.FAILURE,
        });
      } else {
        showToast({
          message: t('Homepage Generation Failed, Please try again later'),
          variant: toastVariant.FAILURE,
        });
      }
    },
    [showToast, t]
  );

  const [deleteBlockModalProps, deleteBlockModalTriggerProps] = useModal({
    handleSubmit: () => {
      deleteDashlet(
        intermediateDeleteState.current.dashboardId,
        intermediateDeleteState.current.dashletId
      );
      intermediateDeleteState.current = {};
    },
  });

  const [
    editDashletModalProps,
    ,
    { show: handleShowEditDashlet, hide: handleHideEditDashlet },
  ] = useModal({
    handleSubmit: async (
      newDashlet,
      _payload,
      isScheduledActivitiesColumnUpdate
    ) => {
      let result;
      if (chartTypes.includes(newDashlet.reportType.value)) {
        result = await mutateChartGroupTypeDashlet({
          dashletData: { ...newDashlet, id: currentDashlet.id },
          customObject: newDashlet.homepageSelectedObjectId,
          isUpdate: true,
          refreshAfterUpdate,
        });
      } else {
        result = await mutateDashletForDashboardOnly({
          dashboardId: currentHomepage.id,
          dashletData: { ...newDashlet, id: currentDashlet.id },
          isUpdate: true,
          layoutUpdate: isScheduledActivitiesColumnUpdate,
          refreshAfterUpdate,
        });
      }
      if (refreshAfterUpdate) {
        onRefreshErrorBoundaryRef.current?.();
        onRefreshErrorBoundaryRef.current = null;
      }
      return result;
    },
  });

  const handlePromptForDeleteDashlet = useCallback(
    (dashboardId, dashletId) => {
      intermediateDeleteState.current = {
        dashboardId,
        dashletId,
      };
      deleteBlockModalTriggerProps.onClick();
    },
    [deleteBlockModalTriggerProps]
  );

  const mutateDashletForDashboardOnly = useCallback(
    async ({
      dashboardId,
      dashletData,
      isUpdate = false,
      layoutUpdate = false,
      isDuplicate = false,
      refreshAfterUpdate = false,
    }) => {
      const oldDashlet = dashlets.find((d) => d.id === dashletData.id);
      const dimensions = getDashletDimensions(dashletData);
      const position = getDashletPosition(dimensions);
      const initialFeExtraInfo = defaultDateFilter
        ? { date_filter: defaultDateFilter }
        : undefined;
      const payload = {
        config: buildDashletConfig(dashletData, { initialFeExtraInfo }),
        layout: {
          i: uuidv4(),
          ...dimensions,
          ...position,
        },
        name: dashletData.name || DEFAULT_NAME_KEY,
      };
      if (isUpdate && oldDashlet) {
        const updatePayload = {
          dashletId: dashletData.id,
          dashboardId,
          payload: {
            config: payload.config,
            name: payload.name || DEFAULT_NAME_KEY,
            layout: {
              i: oldDashlet.layout.i,
              h: oldDashlet.layout.h,
              w: oldDashlet.layout.w,
              x: oldDashlet.layout.x,
              y: oldDashlet.layout.y,
            },
          },
          bustCache: refreshAfterUpdate,
        };
        const { minW, minH, maxW, maxH } = dimensions;
        const { h: oldHeight, w: oldWidth } = oldDashlet.layout;
        if (minW && oldWidth < minW) {
          updatePayload.payload.layout.w = minW;
        }
        if (maxW && oldWidth > maxW) {
          updatePayload.payload.layout.w = maxW;
        }
        if (minH && oldHeight < minH) {
          updatePayload.payload.layout.h = minH;
        }
        if (maxH && oldHeight > maxH) {
          updatePayload.payload.layout.h = maxH;
        }
        updatePayload.layoutUpdate = layoutUpdate;

        await updateDashletMutation.mutateAsync(
          toAiBlockSafeUpdate(updatePayload, oldDashlet, aiSessionDashletId)
        );
      } else {
        if (isDuplicate) {
          await duplicateDashlet({
            payload,
            oldDashlet,
            dashlets,
            dashboardId,
          });
        } else {
          await createDashletMutation.mutateAsync({ dashboardId, payload });
        }
      }
    },
    [
      createDashletMutation,
      updateDashletMutation,
      getDashletPosition,
      dashlets,
      duplicateDashlet,
      defaultDateFilter,
      aiSessionDashletId,
    ]
  );

  const [addDashletModalProps, addDashletModalTriggerProps] = useModal({
    handleSubmit: (chart, builderData) => {
      // We need to remove the background, height, and shadow from the builder configuration
      // because that needs to instead be controlled by the dashlet container. As far
      // as the builder is concerned, the background is always transparent and there's no shadow
      if (!chart) {
        mutateStaticContentBlock({
          dashboardId: currentHomepage.id,
          builderData,
        });
      } else if (chart?.type === AI_BLOCK_TYPE) {
        // The AI sidebar creates the block on the agent's first push
        openAiBlockCreate();
      } else if (chart?.type === 'plugin_block') {
        mutatePluginBlock({
          dashboardId: currentHomepage.id,
          result: chart,
        });
      } else if (responseValues.includes(chart.area)) {
        mutateDashletForDashboardOnly({
          dashboardId: currentHomepage.id,
          dashletData: chart,
          isUpdate: false,
          refreshAfterUpdate,
        });
      } else {
        mutateChartGroupTypeDashlet({
          dashletData: chart,
          isUpdate: false,
          customObject: chart.fieldObject?.id,
          refreshAfterUpdate,
        });
      }
    },
  });

  const [dynamicHeights, setDynamicHeights] = useState({});

  const handleSetDynamicHeight = useCallback(
    (height, dashletId) => {
      const shouldIncreaseHeight = mobile && height > dynamicHeights[dashletId];
      const shouldDecreaseHeight =
        !mobile && height < dynamicHeights[dashletId];

      if (
        !dynamicHeights[dashletId] ||
        shouldIncreaseHeight ||
        shouldDecreaseHeight
      ) {
        setDynamicHeights((prev) => ({
          ...prev,
          [dashletId]: height,
        }));
      }
    },
    [dynamicHeights, mobile]
  );

  const rowHeightOverrides = useMemo(() => {
    if (
      [
        DASHBOARD_BREAKPOINT_NAMES.SM,
        DASHBOARD_BREAKPOINT_NAMES.MD,
        DASHBOARD_BREAKPOINT_NAMES.XS,
        DASHBOARD_BREAKPOINT_NAMES.XXS,
      ].includes(breakpoint)
    ) {
      return Object.keys(dynamicHeights).reduce((acc, dashletId) => {
        // We need to account for the margin when calculating heights
        const margin = 10;
        const newHeight =
          (dynamicHeights[dashletId] + margin) / (rowHeight + margin);
        return {
          ...acc,
          [dashletId]: Math.ceil(newHeight),
        };
      }, {});
    }

    return {};
  }, [dynamicHeights, breakpoint, rowHeight]);

  const renderChild = useCallback(
    ({ dashlet, errorPadding, index }) => {
      return renderAiFocusSlot(
        dashlet,
        draggable && hasEditPermission && isAdmin,
        () => (
          <IntermediateDashlet
            setContentHeight={(height) =>
              handleSetDynamicHeight(height, dashlet.id)
            }
            errorPadding={errorPadding}
            deleteDashlet={handlePromptForDeleteDashlet}
            currentHomepage={currentHomepage}
            dashlet={getDashletWithMergedStyles(dashlet, currentHomepage)}
            isDashletUpdating={
              updateDashletMutation.isLoading &&
              updateDashletMutation.variables.dashletId === dashlet.id
            }
            onDeleteDashlet={handlePromptForDeleteDashlet}
            isDraggable={draggable && hasEditPermission && isAdmin}
            breakpoint={breakpoint}
            setOverrideShowDashletMenu={() => {
              setOverrideShowDashletMenu(true);
              setCurrentDashlet(dashlet);
              deferExecution(() => handleShowEditDashlet());
            }}
            editDashlet={(newDashlet, builderData, isTableColumnUpdate) => {
              if (
                builderData &&
                typeof builderData === 'object' &&
                !newDashlet
              ) {
                return mutateStaticContentBlock({
                  dashboardId: currentHomepage.id,
                  builderData,
                  dashletId: dashlet.id,
                });
              }

              if (chartTypes.includes(newDashlet.reportType.value)) {
                return mutateChartGroupTypeDashlet({
                  dashletData: { ...newDashlet, id: dashlet.id },
                  isUpdate: true,
                  customObject: newDashlet.homepageSelectedObjectId,
                  layoutUpdate: isTableColumnUpdate,
                });
              }

              if (responseValues.includes(newDashlet.area)) {
                return mutateDashletForDashboardOnly({
                  dashboardId: currentHomepage.id,
                  dashletData: {
                    ...newDashlet,
                    id: dashlet.id,
                  },
                  isUpdate: true,
                  layoutUpdate: isTableColumnUpdate,
                });
              }
            }}
            duplicateDashlet={(newDashlet, builderData) => {
              if (isCustomCodeDashlet(dashlet)) {
                return mutateCustomCodeBlock({
                  dashboardId: currentHomepage.id,
                  dashletId: dashlet.id,
                  isDuplicate: true,
                  name: newDashlet.name,
                });
              }
              if (builderData && typeof builderData === 'object') {
                return mutateStaticContentBlock({
                  dashboardId: currentHomepage.id,
                  builderData: dashlet.config.content,
                  dashletId: dashlet.id,
                  isDuplicate: true,
                });
              }
              if (isPluginContentDashlet(dashlet)) {
                return mutatePluginBlock({
                  dashboardId: currentHomepage.id,
                  dashletId: dashlet.id,
                  isDuplicate: true,
                  name: newDashlet.name,
                });
              }
              if (chartTypes.includes(newDashlet.reportType.value)) {
                return mutateChartGroupTypeDashlet({
                  dashletData: { ...newDashlet, id: dashlet.id },
                  isUpdate: false,
                  customObject: dashlet.customObject,
                  isDuplicate: true,
                });
              }

              if (responseValues.includes(newDashlet.area)) {
                return mutateDashletForDashboardOnly({
                  dashboardId: currentHomepage.id,
                  dashletData: {
                    ...newDashlet,
                    id: dashlet.id,
                  },
                  isUpdate: false,
                  isDuplicate: true,
                });
              }
            }}
            onShowEditModal={(
              { isErrorBoundary, onRefresh } = { isErrorBoundary: false }
            ) => {
              onRefreshErrorBoundaryRef.current = onRefresh;
              setRefreshAfterUpdate(isErrorBoundary);
              setOverrideShowDashletMenu(false);
              setAutoOpenAIOverlay(false);
              // An AI block with a stored session opens the AI sidebar in focus
              // mode; without one (or an unparseable one) it falls through to
              // the JSON editor
              if (openAiBlockEdit(dashlet)) {
                return;
              }
              if (isCustomCodeDashlet(dashlet)) {
                openCustomCodeEditor(dashlet);
              } else if (isStaticContentDashlet(dashlet)) {
                openStaticContentBuilder(dashlet);
              } else if (isPluginContentDashlet(dashlet)) {
                setPluginEditDashlet(dashlet);
              } else {
                setCurrentDashlet(dashlet);
                deferExecution(() => handleShowEditDashlet());
              }
            }}
            onEditWithAIHandler={
              dashletAiFeaturesEnabled && !isStaticContentDashlet(dashlet)
                ? () => {
                    setCurrentDashlet(dashlet);
                    deferExecution(() => {
                      handleShowEditDashlet();
                      setAutoOpenAIOverlay(true);
                    });
                  }
                : undefined
            }
            mobile={mobile}
            canEdit={hasEditPermission && isAdmin}
            index={index}
            ranges={ranges}
            todayDateInBusinessTimeZone={todayDateInBusinessTimeZone}
            aiSessionDashletId={aiSessionDashletId}
          />
        )
      );
    },
    [
      renderAiFocusSlot,
      aiSessionDashletId,
      openAiBlockEdit,
      breakpoint,
      currentHomepage,
      mobile,
      dashletAiFeaturesEnabled,
      mutateChartGroupTypeDashlet,
      handlePromptForDeleteDashlet,
      draggable,
      hasEditPermission,
      mutateStaticContentBlock,
      openStaticContentBuilder,
      mutateDashletForDashboardOnly,
      mutatePluginBlock,
      mutateCustomCodeBlock,
      openCustomCodeEditor,
      isAdmin,
      handleSetDynamicHeight,
      handleShowEditDashlet,
      ranges,
      todayDateInBusinessTimeZone,
      updateDashletMutation.isLoading,
      updateDashletMutation.variables,
    ]
  );

  const children = useDashboardGridChildren({
    renderChild,
    forceShowUIElements,
    dashlets,
    defaultStyleSettings: currentHomepage?.styleSettings,
    hasEditPermission: hasEditPermission && isAdmin,
    breakpoint,
    addDashletModalTriggerProps,
    mobile,
    dateFilter: {}, // no global date filters for charts
    mode: MODES.HOMEPAGES,
    rowHeightOverrides,
    columnCount,
  });

  const currentDashletWithDefaultStyles = useMemo(
    () => getDashletWithMergedStyles(currentDashlet, currentHomepage),
    [currentDashlet, currentHomepage]
  );

  const handleClickEditHomepages = useCallback(() => {
    history.push('/home/settings');
  }, [history]);

  const createDashboardMutation = useMutation({
    mutationFn: (result) => createDashboardQuery(result, undefined, 'homepage'),
    onMutate: () => {
      setErrors({});
    },
    onSuccess: (data) => {
      afterCreate(data.id);
      handleHideModal();
      deferExecution(() => {
        history.push(`/home/${data.id}`);
      });
    },
    onError: (error) => {
      setErrors(error?.response?.data?.errors);
    },
  });

  const handleCreateDashboard = useCallback(
    ({ name, permissions, styleSettings }) => {
      createDashboardMutation.mutate({
        name,
        style_settings: styleSettings,
        ...buildSharingSettingsForAPI(permissions),
      });
    },
    [createDashboardMutation]
  );

  const [modalProps, , { show: handleShowModal, hide: handleHideModal }] =
    useModal({
      closeOnSubmit: false,
      handleSubmit: handleCreateDashboard,
    });

  const handleClickAddDashboard = useCallback(() => {
    handleShowModal();
  }, [handleShowModal]);

  const updateDashboardMutation = useHomepageOptimisticUpdate({
    includeSharing: false,
    debouncedTerm: '',
  });

  const [activeStateModalProps, activeStateModalTriggerProps] = useModal({
    handleSubmit: () => {
      updateDashboardMutation.mutate({
        id: currentHomepage.id,
        payload: {
          published: !currentHomepage.published,
        },
      });
    },
  });

  const missingPermission = useMemo(() => {
    if (isLoading) {
      return false;
    }

    if (
      dashboardError?.response?.status === 403 &&
      Boolean(unsafeDashboardId)
    ) {
      return true;
    }

    if (
      !currentHomepage &&
      !isAdmin &&
      !dashboardError &&
      Boolean(unsafeDashboardId)
    ) {
      return true;
    }

    return false;
  }, [isLoading, dashboardError, unsafeDashboardId, currentHomepage, isAdmin]);

  const doesNotExist = useMemo(() => {
    if (isLoading) {
      return false;
    }

    // This is a bit of a hack to get around the issue when the backend
    // rejects the request with a 302 because of a malformed UUID
    if (dashboardError && !dashboardError.response) {
      return Boolean(unsafeDashboardId);
    }

    return (
      dashboardError?.response?.status === 404 && Boolean(unsafeDashboardId)
    );
  }, [isLoading, dashboardError, unsafeDashboardId]);

  const showChartEditModal = useMemo(
    () =>
      Object.values(CHART_AREA_RESPONSES).includes(
        currentDashlet?.config?.reportType
      ),
    [currentDashlet?.config?.reportType]
  );

  const EditDashletModal = useMemo(() => {
    return showChartEditModal ? ChartModal : DashletModal;
  }, [showChartEditModal]);

  const missing = !currentHomepage;
  const unavailable = missingPermission || doesNotExist;

  if (currentHomepage === null && !isLoading && hasDashboards) {
    const fallbackDashboardId = homepageOptions?.filter((d) => !d.hidden)?.[0]
      ?.id;
    if (fallbackDashboardId && fallbackDashboardId !== unsafeDashboardId) {
      showToast({
        message: t(
          'The homepage you tried to view is not accessible to you. Contact your administrator if you think this is in error'
        ),
        variant: toastVariant.FAILURE,
      });
      setCurrentDashboardId(fallbackDashboardId);
      return <Redirect to={`/home/${fallbackDashboardId}`} />;
    }
  }

  if (
    currentHomepage?.type === 'generic_dashboard' ||
    currentHomepage?.type === 'chart_group'
  ) {
    const fallbackDashboardId = homepageOptions[0]?.id;
    setCurrentDashboardId(fallbackDashboardId);
    return (
      <Redirect
        to={getDashboardUrl(
          currentHomepage,
          currentHomepage?.customObject,
          clientObjectId
        )}
      />
    );
  }

  if (isLoading) {
    return <Loader loading />;
  }

  return (
    <DashboardStateWrapper
      setPersistentDashboardState={setPersistentDashboardState}
      persistentDashboardState={persistentDashboardState}
    >
      <OverflowPortalContext.Provider value={overflowPortalContainer}>
        <DashboardGridPageContainer isMobile={mobile}>
          {(homepageOptions.length > 1 || isAdmin) && mobile ? (
            <StyledSubNavLabel
              options={homepageOptions}
              value={
                missing || unavailable
                  ? {
                      name: unavailable ? t('Unavailable') : t('None'),
                    }
                  : currentHomepage
              }
              onChange={(id) => history.push(`/home/${id}`)}
              classNamePrefix="homepage-menu"
              buildHref={(id) => `/home/${id}`}
              entitySingular={t('Homepage')}
              entityPlural={t('Homepages')}
              mobile
            />
          ) : null}
          {(homepageOptions.length > 1 ||
            isAdmin ||
            (unavailable && homepageOptions.length > 0)) &&
          !mobile ? (
            <SubNavigationBar ref={aiOverlayContainerRef}>
              {homepageOptions.length > 1 ||
              (unavailable && homepageOptions.length > 0) ||
              isAdmin ? (
                <StyledSubNavLabel
                  options={homepageOptions}
                  value={
                    missing || unavailable
                      ? {
                          name: unavailable ? t('Unavailable') : t('None'),
                        }
                      : currentHomepage
                  }
                  onChange={(id) => history.push(`/home/${id}`)}
                  handleClickEditDashboards={handleClickEditHomepages}
                  handleClickAddDashboard={handleClickAddDashboard}
                  components={
                    showSettings
                      ? {
                          Menu: EditDashboardsButton,
                        }
                      : undefined
                  }
                  classNamePrefix="homepage-menu"
                  buildHref={(id) => `/home/${id}`}
                  entitySingular={t('Homepage')}
                  entityPlural={t('Homepages')}
                />
              ) : null}
              {isAdmin && (
                <StyledNav
                  data-qa="homepage-nav"
                  data-qa-settings-visible={showSettings}
                  data-qa-add-block-visible={
                    currentHomepage && hasEditPermission && isAdmin
                  }
                >
                  {currentHomepage ? (
                    <SwitchContainer>
                      <StyledSwitch
                        checked={currentHomepage?.published}
                        onClick={activeStateModalTriggerProps.onClick}
                        label={t('Active?')}
                        textPlacement="left"
                      />
                    </SwitchContainer>
                  ) : null}
                  {aiFeaturesEnabled && showSettings ? (
                    <SinglePromptPopoverWrapper>
                      <AiExpandableSinglePrompt
                        flowType={FLOW_TYPE.homepage_builder_inline}
                        taskType="dashboard_plan_generation"
                        builderScope="homepage"
                        context={{}}
                        onOneShotSuccess={onGenerationSuccess}
                        onChatSessionSuccess={onGenerationSuccess}
                        onGenerationFailure={onGenerationFailure}
                        entityName={t('Homepage')}
                        extractGenerationResult={extractGenerationResult}
                        messagesConfig={{
                          placeholder: t('Create a homepage using AI'),
                          buildingMessage: t('Building your homepage'),
                          successMessage: t('Homepage Generated Successfully'),
                          failureMessage: t(
                            'Homepage Generation Failed, Please try again later'
                          ),
                          expandedPlaceholder: t('Add additional context'),
                          closingMessage: t('Closing session'),
                          initialMessage: t(
                            'What type of homepage would you like to create?'
                          ),
                        }}
                        importResults={true}
                        popoverProps={{
                          container: aiOverlayContainerRef.current,
                          placement: 'bottom-end',
                        }}
                        inModal
                      />
                    </SinglePromptPopoverWrapper>
                  ) : null}
                  <Button color="blue" onClick={handleClickEditHomepages}>
                    {t('Manage Homepages')}
                  </Button>
                  {currentHomepage && hasEditPermission && isAdmin ? (
                    <Button {...addDashletModalTriggerProps}>
                      {t('Add Block')}
                    </Button>
                  ) : null}
                </StyledNav>
              )}
            </SubNavigationBar>
          ) : null}
          <PiChatSideLayout panel={aiBlockPanel}>
            <DashboardGridContainerBox>
              <BreakpointContainer className="breakpoint-container">
                {isFlagEnabled('grid-breakpoints') ? (
                  <div className="bp-container">
                    <div className="bp bp-xxs">XXS</div>
                    <div className="bp bp-xs">XS</div>
                    <div className="bp bp-sm">SM</div>
                    <div className="bp bp-md">MD</div>
                    <div className="bp bp-lg">LG</div>
                    <div className="bp bp-xl">XL</div>
                    <div className="bp bp-xxl">XXL</div>
                  </div>
                ) : null}
                {!currentHomepage && (
                  <>
                    {missingPermission ? (
                      <ErrorMessage
                        title={t('Permissions Needed')}
                        text={
                          <DashboardWarningText as="p">
                            {t(
                              'The page you are trying to view requires special permissions or is no longer active. If you believe you should have permissions to view, please contact your Business Administrator.'
                            )}
                          </DashboardWarningText>
                        }
                        code={
                          <DashboardErrorCodeText as="p">
                            {t('Error code')}: 403
                          </DashboardErrorCodeText>
                        }
                        dashboard
                      />
                    ) : doesNotExist ? (
                      <ErrorMessage
                        title={t('This Page No Longer Exists')}
                        text={
                          <DashboardWarningText as="p">
                            {t(
                              'The page you are trying to reach no longer exists.'
                            )}
                            {isAdmin
                              ? ` ${t(
                                  'Please select another homepage to view and edit or use the button below to create a new homepage.'
                                )}`
                              : ''}
                            {homepageOptions.length > 0 && !isAdmin
                              ? ` ${t(
                                  'Please select another homepage from the dropdown on the top left.'
                                )}`
                              : ''}
                          </DashboardWarningText>
                        }
                        dashboard
                        button={
                          isAdmin ? (
                            <Button
                              color="green"
                              onClick={handleClickAddDashboard}
                            >
                              {t('Add Homepage')}
                            </Button>
                          ) : null
                        }
                      />
                    ) : !hasDashboards ? (
                      <ErrorMessage
                        title={t('No Homepages Available')}
                        text={
                          isAdmin ? (
                            <DashboardWarningText as="p">
                              {t(
                                'There are currently no homepages created for this business. To get started, please click the Homepage Settings button at the top right of the screen or the Add Homepage button below.'
                              )}
                            </DashboardWarningText>
                          ) : (
                            <DashboardWarningText as="p">
                              {t(
                                "There are currently no homepages created for this business. Please contact your administrator if you believe you're seeing this page in error."
                              )}
                            </DashboardWarningText>
                          )
                        }
                        button={
                          isAdmin ? (
                            <Button
                              color="green"
                              onClick={handleClickAddDashboard}
                            >
                              {t('Add Homepage')}
                            </Button>
                          ) : null
                        }
                        dashboard
                      />
                    ) : null}
                  </>
                )}
                <DashboardGrid
                  updateLayouts={updateLayouts}
                  resizeHandles={isAdmin ? resizeHandles : []}
                  rowHeight={rowHeight}
                  editable={editable && isAdmin}
                  setBreakpoint={setBreakpoint}
                  setContainerWidth={setContainerWidth}
                >
                  {children}
                </DashboardGrid>
              </BreakpointContainer>
            </DashboardGridContainerBox>
          </PiChatSideLayout>
        </DashboardGridPageContainer>
        <BlockModal
          {...addDashletModalProps}
          currentDashboard={currentHomepage}
        />
        {currentDashlet?.id &&
        !hasEditPermission &&
        !editDashletModalProps.show ? null : (
          <EditDashletModal
            {...editDashletModalProps}
            homepage
            currentDashboard={currentHomepage}
            existing={
              currentDashletWithDefaultStyles.id
                ? editableDashlet(currentDashletWithDefaultStyles, t)
                : {}
            }
            scrollActvitiyBuilderIntoView={overrideShowDashletMenu}
            autoOpenAIOverlay={autoOpenAIOverlay}
            hideModal={() => {
              setOverrideShowDashletMenu(false);
              setAutoOpenAIOverlay(false);
              handleHideEditDashlet();
              setCurrentDashlet({});
            }}
            clientObjectId={clientObjectId}
            objectId={currentDashlet?.customObject}
            contacts={clientObjectId === currentDashlet?.customObject}
            model={currentDashletModel}
          />
        )}
        {pluginEditDashlet && (
          <PluginBlockWizard
            dashlet={pluginEditDashlet}
            mode={MODES.HOMEPAGES}
            onHide={() => setPluginEditDashlet(null)}
            onConfirm={(result) => {
              setPluginEditDashlet(null);
              mutatePluginBlock({
                dashboardId: currentHomepage.id,
                dashletId: pluginEditDashlet.id,
                result,
              });
            }}
          />
        )}
        {staticContentBuilderOpen && (
          <HomepageBuilder {...staticContentBuilderProps} />
        )}
        {customCodeEditorProps && (
          <CustomCodeBlockEditor {...customCodeEditorProps} />
        )}
        <ActiveStateModal
          active={currentHomepage?.published}
          {...activeStateModalProps}
        />
        <DeleteConfirmationModal {...deleteBlockModalProps} />
        <DashboardStyleSettingsProvider
          styleSettings={DEFAULT_DASHLET_STYLE_SETTINGS}
        >
          <CreateLayoutModal
            {...modalProps}
            canUserEditPermissions
            showPrivateToggle={false}
            entity={t('Homepage')}
            errors={errors}
            blockHideIfDirty
            forceAdmin
          />
        </DashboardStyleSettingsProvider>
      </OverflowPortalContext.Provider>
      <PortalContainer ref={setOverflowPortalContainer} zIndex={1} />
    </DashboardStateWrapper>
  );
};

const HomepagesPageWithDataManager = (props) => {
  const { id } = useParams();

  if (!id) {
    return <HomepagesPage {...props} />;
  }

  return (
    <DataManagerWrapper key={id} instanceId={id}>
      <HomepagesPage {...props} id={id} />
    </DataManagerWrapper>
  );
};

export default HomepagesPageWithDataManager;
